// src/context/AuthContext.js
//
// React context for SIWE auth. Provides:
//   - isAuthenticated: boolean
//   - authToken: current JWT or null
//   - sessionWallet: wallet bound to current session
//   - ensureAuth(): runs login flow if not authenticated, returns token.
//     Concurrent calls share ONE login (one signature popup).
//   - logout(): clears session
//
// Wallet switch / disconnect clears the session; user re-signs on next action.
//
// EMAIL SESSIONS (mission 007, REACT_APP_EMAIL_SIGNIN=on): the session may be
// an email sign-in bound to the account's managed address (no wagmi account,
// nothing can sign). Then: isAuthenticated = an unexpired token; a wagmi
// disconnect never touches it; a wagmi wallet CONNECTING ends it (no linking
// at launch, never a silent second account); logout() also signs out of
// Supabase Auth and disconnects wagmi; on load a persisted Supabase Auth
// session without a Lunexx token signs in silently (never creating an account).
//
// PROVIDER ORDER (mission 009): AuthSiweProvider wraps WalletProvider, so the
// sign-in modals WalletProvider renders can read this context (their "Confirm
// it's you" step calls ensureAuth from a tap). Nothing here reads WalletContext.

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAccount, useSignMessage, useChainId, useDisconnect } from 'wagmi';
import { authService, hasConnectIntent, clearConnectIntent } from '../services/AuthService';
import { emailSignInEnabled } from '../config/emailSignIn';
import { endEmailSession, restoreEmailSession } from '../lib/emailSession';
import { useAuthSession } from '../hooks/useAuthSession';

const AuthContext = createContext(null);

export const AuthSiweProvider = ({ children }) => {
  const { address, status: accountStatus, connector } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const chainId = useChainId();
  const { disconnectAsync } = useDisconnect();
  const flagged = emailSignInEnabled();
  const session = useAuthSession();
  const emailSession = flagged && session?.method === 'email' ? session : null;

  const [authToken, setAuthToken] = useState(null);
  const [sessionWallet, setSessionWallet] = useState(null);
  const [loginInFlight, setLoginInFlight] = useState(false);

  // Single in-flight login promise — concurrent ensureAuth calls await
  // the same login instead of double-prompting or throwing.
  const loginPromiseRef = useRef(null);

  // Sync token state from the service. Runs on address change (fixes the
  // "blind until first poll" gap when wagmi restores the connection async)
  // and every 30s as an expiry sweeper.
  // (re-run on every session change too: an email sign-in has no address change)
  const syncFromService = useCallback(() => {
    setAuthToken(authService.getToken(address) || null);
    setSessionWallet(authService.getSessionWallet() || null);
  }, [address, session]);   // eslint-disable-line react-hooks/exhaustive-deps -- session is the change signal

  useEffect(() => {
    syncFromService();
    const interval = setInterval(syncFromService, 30_000);
    return () => clearInterval(interval);
  }, [syncFromService]);

  // Wallet switch or disconnect → invalidate the bound session.
  //
  // IMPORTANT: on page load wagmi reports isConnected === false while it
  // asynchronously restores the connection (status 'connecting' /
  // 'reconnecting'). The old code treated that window as a disconnect and
  // logged out on EVERY refresh, forcing a new SIWE signature. Only treat
  // it as a disconnect once wagmi has settled on status === 'disconnected'.
  useEffect(() => {
    // an email session: only a wagmi wallet CONNECTING ends it
    if (flagged && authService.getSession()?.method === 'email') {
      if (address && accountStatus === 'connected') {
        endEmailSession();
        setAuthToken(null);
        setSessionWallet(null);
      }
      return;
    }
    const bound = authService.getSessionWallet();
    if (!bound) return;
    const switchedWallet = address && bound !== address.toLowerCase();
    const trulyDisconnected = accountStatus === 'disconnected';
    if (switchedWallet || trulyDisconnected) {
      authService.logout();
      setAuthToken(null);
      setSessionWallet(null);
    }
  }, [address, accountStatus, flagged]);

  // on load: a persisted Supabase Auth session without a Lunexx token signs in silently
  useEffect(() => {
    if (!flagged || authService.getSession()) return;
    restoreEmailSession();
  }, [flagged]);

  const ensureAuth = useCallback(async () => {
    if (!address && flagged) {
      const s = authService.getSession();
      if (s?.method === 'email') return s.token;
      const restored = await restoreEmailSession();
      if (restored) return restored;
    }
    if (!address) throw new Error('Wallet not connected');

    const existing = authService.getToken(address);
    if (existing) return existing;

    // Join an in-flight login instead of starting a second one
    if (loginPromiseRef.current) return loginPromiseRef.current;

    const promise = (async () => {
      setLoginInFlight(true);
      try {
        const result = await authService.login(address, signMessageAsync, { chainId });
        setAuthToken(result.token);
        setSessionWallet(result.walletAddress);
        return result.token;
      } finally {
        setLoginInFlight(false);
        loginPromiseRef.current = null;
      }
    })();

    loginPromiseRef.current = promise;
    return promise;
  }, [address, signMessageAsync, chainId, flagged]);

  // Connect = sign in, for EXTENSION wallets (connector type 'injected'): a
  // connect the user started (markConnectIntent in the connect modal /
  // connectWallet; wagmi 'connecting' → 'connected') asks for the SIWE
  // signature right away. An extension's popup is not subject to the browser's
  // popup blocker. A POPUP wallet (Coinbase Smart Wallet) opens a new window for
  // every request, the browser grants one popup per user activation and the
  // connect click spent it: a request from here is blocked (mission 009). For
  // those nothing is attempted — the modal's "Confirm it's you" step stays open
  // and its Sign in tap is the request.
  // A connection wagmi restores by itself never prompts; pages that need a
  // session ask from a tap (e.g. the chat page's Sign in). A cancelled
  // signature leaves the wallet connected and unsigned.
  // Keyed on the intent, not on observing 'connecting': a wallet that is
  // already authorised can go connecting → connected inside one React batch.
  // An account switch inside the wallet (e.g. MetaMask: creator account →
  // fan account) is the same thing: the old session is dropped above, so the
  // new account is asked to sign right away instead of landing on a gate.
  const prevAddressRef = useRef(address);
  useEffect(() => {
    const prev = prevAddressRef.current;
    prevAddressRef.current = address;
    if (accountStatus !== 'connected' || !address) return;
    const switched = !!prev && prev.toLowerCase() !== address.toLowerCase();
    if (!switched && !hasConnectIntent()) return;
    clearConnectIntent();
    if (connector?.type !== 'injected') return;
    if (authService.getToken(address)) return;
    ensureAuth().catch((e) => console.warn('[auth] sign-in after connect did not complete:', e?.message || e));
  }, [accountStatus, address, ensureAuth, connector]);

  const logout = useCallback(() => {
    authService.logout();
    setAuthToken(null);
    setSessionWallet(null);
    if (flagged) {
      endEmailSession();
      if (accountStatus === 'connected') disconnectAsync().catch(() => {});
    }
  }, [flagged, accountStatus, disconnectAsync]);

  const value = useMemo(() => ({
    isAuthenticated: emailSession ? !!authToken : !!authToken && sessionWallet === address?.toLowerCase(),
    authMethod: emailSession ? 'email' : (authToken ? 'wallet' : null),
    email: emailSession?.email ?? null,
    authToken,
    sessionWallet,
    loginInFlight,
    ensureAuth,
    logout,
    secondsUntilExpiry: () => authService.secondsUntilExpiry(),
  }), [authToken, sessionWallet, address, loginInFlight, ensureAuth, logout, emailSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useSiweAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useSiweAuth must be used inside <AuthSiweProvider>');
  return ctx;
};